import { Box, Container, Typography } from '@mui/material';
import sanitizeHtml from 'sanitize-html';
import he from 'he';
import { Carousel } from '@/components/common/Carousel';
import { useSearchDirectoryData } from '@/context/searchDirectory/SearchDirectoryDataContext';
import { useResponsive } from '@/components/hooks/useResponsive';
import { SearchDirectoryTemplateType } from '@/constants';
import { usePageAttributes } from '@/components/contexts/PageAttributesContext';
import { SEOSegmentationPageType } from '@/__generated__/globalTypes';
import { ReviewCard } from './ReviewCard';
import { useSearchResultReviews } from './useSearchResultReviews';

type ReviewsSectionProps = {
  searchDirectoryTemplateType: SearchDirectoryTemplateType;
  slotProps?: {
    headline?: { 'data-testid': string };
    subheadline?: { 'data-testid': string };
  };
};

export const ReviewsSection = ({ searchDirectoryTemplateType, slotProps }: ReviewsSectionProps) => {
  const pageData = useSearchDirectoryData();
  const { seoSegmentationSearchResults } = pageData;
  const segmentationStats =
    pageData.seoSegmentationStats?.__typename === 'SEOSegmentationStatsSuccess'
      ? pageData.seoSegmentationStats.caregiverStats
      : null;
  const { isSmallViewport } = useResponsive();
  const {
    segmentNameSingularLowerCase,
    segmentNamePluralLowerCase,
    segmentNamePlural,
    city,
    state,
    pageType,
  } = usePageAttributes();

  const reviewsList = useSearchResultReviews(
    seoSegmentationSearchResults,
    searchDirectoryTemplateType
  );

  const avgReviewRating = segmentationStats?.avgReviewRating?.toFixed(1) ?? '';
  const totalReviews = segmentationStats?.totalReviewCount?.toLocaleString() ?? '';
  const totalProviders = segmentationStats?.totalProviders?.toLocaleString() ?? '';

  const showSubheadline =
    avgReviewRating.length > 0 && totalReviews.length > 0 && totalProviders.length > 0;

  const headline = (() => {
    if (pageType === SEOSegmentationPageType.LOCAL) {
      return `Recent ${segmentNameSingularLowerCase} reviews from local parents in ${city},&nbsp;${state}`;
    }

    return `Recent ${segmentNameSingularLowerCase} reviews from local parents`;
  })();

  const subheadline = (() => {
    if (pageType === SEOSegmentationPageType.LOCAL) {
      return `${segmentNamePlural} in ${city},&nbsp;${state} are rated ${avgReviewRating} out of 5 stars based on ${totalReviews} reviews of the ${totalProviders} listed ${segmentNamePluralLowerCase}`;
    }

    return `${segmentNamePlural} are rated ${avgReviewRating} out of 5 stars based on ${totalReviews} reviews of the ${totalProviders} listed ${segmentNamePluralLowerCase}`;
  })();

  if (!reviewsList || reviewsList.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        bgcolor: 'background.accent.accent1',
      }}>
      <Container
        maxWidth="lg"
        sx={{
          py: { xs: 6, sm: 8 },
        }}>
        <Typography
          {...slotProps?.headline}
          component="h2"
          variant={isSmallViewport ? 'h1' : 'display3'}
          sx={{
            mt: 0,
            mb: { xs: 3, sm: 2 },
          }}
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(he.decode(headline)),
          }}
        />
        {showSubheadline && (
          <Typography
            {...slotProps?.subheadline}
            component="p"
            variant="body2"
            sx={{
              mb: { xs: 3, sm: 6 },
              maxWidth: { sm: '80%' },
            }}
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(he.decode(subheadline)),
            }}
          />
        )}
        <Carousel
          showProgressIndicator={isSmallViewport}
          itemsPerView={isSmallViewport ? 1 : 3}
          controlVariant={isSmallViewport ? 'none' : 'topRight'}>
          {reviewsList.map(({ revieweeName, reviewerName, comment, rating }) => (
            <ReviewCard
              key={`${revieweeName}-${reviewerName}`}
              revieweeName={revieweeName}
              reviewerName={reviewerName}
              comment={comment}
              rating={rating}
            />
          ))}
        </Carousel>
      </Container>
    </Box>
  );
};
